import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  WarehouseItem,
  WarehouseMovementType,
  MOVEMENT_TYPE_LABELS,
  useCreateMovement,
} from '@/hooks/warehouse';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';

interface RegisterMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouseItem: WarehouseItem | null;
}

const MOVEMENT_TYPES: WarehouseMovementType[] = [
  'entrada_compra',
  'salida_propiedad',
  'ajuste_merma',
  'devolucion',
];

const RegisterMovementModal = ({ isOpen, onClose, warehouseItem }: RegisterMovementModalProps) => {
  const { mutate: createMovement, isPending } = useCreateMovement();

  const isSubtraction = (type: WarehouseMovementType) =>
    type === 'salida_propiedad' || type === 'ajuste_merma';

  const maxQuantity = warehouseItem?.quantity || 0;

  const formSchema = useMemo(() => {
    return z.object({
      movement_type: z.enum(['entrada_compra', 'salida_propiedad', 'ajuste_merma', 'devolucion'] as const),
      quantity: z.coerce.number().min(1, 'La cantidad debe ser al menos 1')
        .superRefine((val, ctx) => {
          // This allows accessing the sibling fields via the refine wrapper data if needed,
          // but here we are in a field refinement.
          // Wait, superRefine on the field only sees the field value.
          // We need access to movement_type to know if we should check max.
          // So we should do superRefine on the object, OR we just check blindly?
          // No, we need to know the type.
        }),
      unit_price: z.coerce.number().min(0, 'El precio no puede ser negativo'),
      notes: z.string().optional(),
    }).superRefine((data, ctx) => {
      if (isSubtraction(data.movement_type) && data.quantity > maxQuantity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `La cantidad no puede exceder el stock disponible (${maxQuantity})`,
          path: ['quantity'],
        });
      }
    });
  }, [maxQuantity]);

  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      movement_type: 'entrada_compra',
      quantity: 0,
      unit_price: 0,
      notes: '',
    },
  });

  const movementType = form.watch('movement_type');

  // Reset form and pre-fill unit price when modal opens
  useEffect(() => {
    if (isOpen && warehouseItem) {
      form.reset({
        movement_type: 'entrada_compra',
        quantity: 0,
        unit_price: warehouseItem.unit_price,
        notes: '',
      });
    }
  }, [isOpen, warehouseItem, form]);

  const handleSubmit = (data: FormData) => {
    if (!warehouseItem) return;

    createMovement(
      {
        warehouse_item_id: warehouseItem.id,
        movement_type: data.movement_type,
        quantity: data.quantity,
        unit_price: data.unit_price,
        notes: data.notes?.trim() || null,
      },
      { onSuccess: onClose }
    );
  };

  const isCurrentSubtraction = isSubtraction(movementType);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar Movimiento"
      description={`${warehouseItem?.product?.name || ''} - Stock actual: ${warehouseItem?.quantity || 0}`}
      form={form}
      onSubmit={handleSubmit}
      submitLabel="Registrar"
      isLoading={isPending}
      maxWidth="max-w-[500px]"
    >
      <div className="grid gap-4 py-4">
        <FormField
          control={form.control}
          name="movement_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Movimiento</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {MOVEMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {MOVEMENT_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="quantity"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cantidad</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    max={isCurrentSubtraction ? maxQuantity : undefined}
                    placeholder="0"
                    {...field}
                  />
                </FormControl>
                {isCurrentSubtraction && (
                  <p className="text-xs text-muted-foreground">
                    Máximo disponible: {maxQuantity}
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="unit_price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Precio Unitario</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" min="0" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notas (opcional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Notas adicionales..."
                  rows={2}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </FormModal>
  );
};

export default RegisterMovementModal;
